<template>
    <div
        v-show="Boolean(scrollInfo.thumbSize) && (fixedThumb ? isWrapperIntersecting : true)"
        ref="thumbEl"
        class="thumbPlaceHolder"
        :class="[$style.thumbPlaceHolder, $style[direction], $style[`theme-${theme}`], { [$style.autoHide]: autoHide }]"
        :style="{
            width: direction === 'horizontal' ? `${scrollInfo.thumbSize}px` : '',
            height: direction === 'vertical' ? `${scrollInfo.thumbSize}px` : '',
            position: !shouldFixed ? 'absolute' : 'fixed',
            [direction === 'vertical' ? 'top' : 'left']: !shouldFixed ? '3px' : `${scrollInfo.boundaryDistance + 3}px`,
            right: rightOffset,
        }"
        :data-thumbPlaceHolder="direction"
        @pointerdown.stop="handlePointerDown"
    >
        <div v-once class="thumb" :class="[$style.thumb, $style[direction]]" data-thumb />
    </div>
    <div
        v-if="props.fixedThumb"
        v-show="Boolean(scrollInfo.thumbSize)"
        ref="shepherdEl"
        :class="[$style.shepherd, $style[direction]]"
    />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, shallowRef } from 'vue';
import throttle from 'lodash/throttle.js';

interface Props {
    direction: 'horizontal' | 'vertical';
    autoHide: boolean;
    autoHideDelay: number;
    fixedThumb: boolean;
    theme: 'white' | 'dark';
    scrollInfo: {
        thumbSize: number;
        contentMainSize: number;
        wrapperMainSize: number;
        boundaryDistance: number;
    };
    rightOffset: string;
}
const props = defineProps<Props>();

let wrapperEl: HTMLDivElement = null!;
const thumbEl = shallowRef<HTMLDivElement>(null!);
const pointerDownInfo = { pageX: 0, pageY: 0, scrollPos: 0 };
let autoHideTimer: NodeJS.Timeout | null = null;
let pointerId: number | null = null;

const handlePointerMove = throttle((evt: PointerEvent) => {
    evt.stopPropagation();
    evt.preventDefault();
    const moveDirection = props.direction === 'horizontal' ? 'pageX' : 'pageY';
    const moveDistance =
        ((evt[moveDirection] - pointerDownInfo[moveDirection]) / props.scrollInfo.wrapperMainSize) *
        props.scrollInfo.contentMainSize;
    wrapperEl.scrollTo({
        [props.direction === 'horizontal' ? 'left' : 'top']: pointerDownInfo.scrollPos + moveDistance,
        behavior: 'auto',
    });
}, 16);

const handlePointerEnd = () => {
    autoHideTimer = setTimeout(() => {
        thumbEl.value.dataset.scrolling = 'false';
        autoHideTimer = null;
    }, props.autoHideDelay);

    thumbEl.value.removeEventListener('pointermove', handlePointerMove);
    thumbEl.value.removeEventListener('pointerup', handlePointerEnd);
    thumbEl.value.removeEventListener('pointercancel', handlePointerEnd);
    thumbEl.value.removeEventListener('mousewheel', handlePointerEnd);
    document.removeEventListener('mousewheel', handlePointerEnd);
    if (typeof pointerId === 'number') thumbEl.value.releasePointerCapture(pointerId);
    pointerId = null;
};

const handlePointerDown = (evt: PointerEvent) => {
    if (evt.ctrlKey || evt.button !== 0) return;
    if (autoHideTimer !== null) clearTimeout(autoHideTimer);

    pointerDownInfo.pageX = evt.pageX;
    pointerDownInfo.pageY = evt.pageY;
    pointerDownInfo.scrollPos = wrapperEl[props.direction === 'horizontal' ? 'scrollLeft' : 'scrollTop'];

    pointerId = evt?.pointerId;
    thumbEl.value.setPointerCapture(pointerId);

    thumbEl.value.addEventListener('pointermove', handlePointerMove);
    thumbEl.value.addEventListener('pointerup', handlePointerEnd);
    thumbEl.value.addEventListener('pointercancel', handlePointerEnd);
    thumbEl.value.addEventListener('mousewheel', handlePointerEnd, { passive: false });
    document.addEventListener('mousewheel', handlePointerEnd, { passive: false });
    thumbEl.value.dataset.scrolling = 'true';
};

const autoHideAfterScroll = () => {
    if (autoHideTimer !== null) clearTimeout(autoHideTimer);

    if (thumbEl) thumbEl.value.dataset.scrolling = 'true';
    autoHideTimer = setTimeout(() => {
        if (thumbEl) thumbEl.value.dataset.scrolling = 'false';
        autoHideTimer = null;
    }, props.autoHideDelay);
};

let isWrapperIntersecting = shallowRef(false);
let isShepherdIntersecting = shallowRef(false);
let shepherdEl = shallowRef<HTMLElement>(null!);
let shepherdIO: IntersectionObserver | null = null;
let wrapperIO: IntersectionObserver | null = null;
let shouldFixed = computed(() => props.fixedThumb && !isShepherdIntersecting.value);
const shepherdIOCallback: IntersectionObserverCallback = ([entry]) =>
    (isShepherdIntersecting.value = entry.isIntersecting);
const wrapperIOCallback: IntersectionObserverCallback = ([entry]) =>
    (isWrapperIntersecting.value = entry.isIntersecting);

onMounted(() => {
    wrapperEl = thumbEl.value?.parentElement?.querySelector(`[data-scrollWrapper]`) as HTMLDivElement;
    if (!props.fixedThumb) return;
    wrapperIO = new IntersectionObserver(wrapperIOCallback, { threshold: [0, 0.5] });
    wrapperIO.observe(wrapperEl);
    shepherdIO = new IntersectionObserver(shepherdIOCallback);
    shepherdIO.observe(shepherdEl.value);
});

onUnmounted(() => {
    if (autoHideTimer !== null) clearTimeout(autoHideTimer);
    if (shepherdIO) {
        shepherdIO.disconnect();
        shepherdIO = null;
    }
    if (wrapperIO) {
        wrapperIO.disconnect();
        wrapperIO = null;
    }
});

defineExpose({
    autoHideAfterScroll,
});
</script>

<style lang="scss" module>
@import '../../styles/colors';
.thumbPlaceHolder {
    position: absolute;
    display: inline-flex;
    justify-content: center;
    user-select: none;
    cursor: pointer;
    z-index: 50;
    contain: strict;
    transition: opacity 275ms ease;

    &.vertical {
        top: 3px;
        right: 0;

        width: 12px;
    }
    &.horizontal {
        flex-direction: column;
        left: 3px;
        bottom: 0;

        height: 12px;
    }

    &.autoHide {
        opacity: 0;
    }

    &[data-scrolling='true'] {
        opacity: 1;
    }

    &.theme-dark {
        > .thumb {
            background-color: $pt-white-a-64;
        }
        &:hover {
            > .thumb {
                background-color: $pt-white-a-88;
            }
        }
    }

    &:hover {
        > .thumb {
            background-color: rgba(9, 30, 66, 0.5);
            border-radius: 6px;
            &.vertical {
                width: 12px;
                will-change: width, border-radius, background-color;
            }
            &.horizontal {
                height: 12px;
                will-change: height, border-radius, background-color;
            }
        }
    }
}

.thumb {
    background-color: rgba(9, 30, 66, 0.3);
    transition: background-color 275ms ease, width 200ms ease, height 200ms ease, border-radius 200ms ease;
    contain: strict;

    border-radius: 4px;
    &.vertical {
        width: 8px;
        height: 100%;
    }
    &.horizontal {
        height: 8px;
        width: 100%;
    }
}

.shepherd {
    position: absolute;
    &.vertical {
        width: 0%;
        height: 1000;
        top: 0;
        right: 0;
    }
    &.horizontal {
        width: 100%;
        height: 0%;
        bottom: 0;
        left: 0;
    }
    contain: strict;
    content-visibility: auto;
}
</style>
